import { useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import { logger } from '@tankef/env';

import { useSettingsStore } from '../../stores';

/**
 * Syncs help settings (tours on/off, help button visibility) with the backend.
 * Persists to localStorage immediately via settingsStore; backend call is
 * best-effort — a failure never blocks the local update.
 */
export function useHelpSettings() {
  const queryClient = useQueryClient();
  const { help, updateHelp } = useSettingsStore();

  const mutation = useMutation({
    mutationFn: async (settings: Partial<typeof help>) => {
      return clientApi.updateUIPreferences?.({
        help: {
          tours_enabled: settings.toursEnabled,
          help_button_visible: settings.helpButtonVisible,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'ui-preferences'] });
    },
    onError: err => {
      logger.warn('[HelpSettings] Backend sync failed (local state preserved):', err);
    },
  });

  const update = (settings: Partial<typeof help>) => {
    updateHelp(settings);
    mutation.mutate(settings);
  };

  return { help, update, isPending: mutation.isPending };
}
